/**
 * Fez Component Registration & Connection
 *
 * This file handles:
 * - Registering components with customElements
 * - Transforming plain classes to FezBase subclasses
 * - Instantiating components when they appear in DOM
 *
 * Flow:
 * 1. connect(name, class) - registers custom element
 * 2. connectedCallback() - when element appears in DOM
 * 3. connectNode() - creates instance, renders, calls lifecycle
 */

import createTemplate from './lib/template.js';
import closeCustomTags from './lib/close-custom-tags.js';
import FezBase from './instance.js';

// =============================================================================
// MAIN CONNECT FUNCTION
// =============================================================================

/**
 * Register a Fez component
 *
 * @param {string} name - Custom element name (must contain dash)
 * @param {Class} klass - Component class
 *
 * @example
 * Fez('ui-button', class {
 *   HTML = '<button><slot /></button>'
 *   CSS = 'button { color: blue; }'
 *   init() { console.log('created') }
 * })
 */
export default function connect(name, klass) {
  const Fez = globalThis.window?.Fez || globalThis.Fez;

  // Validate name
  if (!name.includes('-')) {
    console.error(`Fez: Invalid name "${name}". Must contain a dash.`);
    return;
  }

  // Transform to FezBase subclass
  klass = ensureFezBase(Fez, name, klass);

  // Process HTML template
  if (klass.html) {
    if (/<slot\s[^>]*unwrap[\s>\/]/.test(klass.html)) {
      klass.fezSlotUnwrap = true;
    }

    // The wrapper div is the morph anchor for slot content, not legacy cruft.
    // Slotted children are external light DOM injected once - they never appear
    // in the template output. fez-keep lets the differ match the empty .fez-slot
    // placeholder on each re-render and preserve the captured children in place.
    // Drop the wrapper (unwrap) and there is nothing for the differ to match, so
    // the content gets removed on the next render - which is why unwrap disables
    // this.state. unwrap exists for layout transparency (flex/grid/ul/table), at
    // the cost of reactivity; the two modes are a trade, not redundancy.
    klass.html = klass.html
      .replace(/<slot(\s[^>]*)?>/, '<div class="fez-slot" fez-keep="default-slot"$1>')
      .replace('</slot>', '</div>');

    klass.fezHtmlFunc = createTemplate(klass.html, { name });
  }

  // Register CSS. Scoped block gets the component name so :fez resolves;
  // the global block is injected verbatim. Injection dedupes on the text, so
  // these stay the source CSS and re-registering is a no-op.
  if (klass.css) {
    Fez.globalCss(klass.css, { name });
  }

  if (klass.cssGlobal) {
    Fez.globalCss(klass.cssGlobal);
  }

  // Store class in index
  Fez.index.ensure(name).class = klass;

  // Register custom element
  if (!customElements.get(name)) {
    customElements.define(
      name,
      class extends HTMLElement {
        connectedCallback() {
          if (document.readyState === 'loading') {
            requestAnimationFrame(() => connectNode(name, this));
          } else {
            connectNode(name, this);
          }
        }
      },
    );
  }
}

// =============================================================================
// CLASS TRANSFORMATION
// =============================================================================

/**
 * Transform plain class to FezBase subclass
 * Maps uppercase config props (HTML, CSS, etc.)
 */
function ensureFezBase(Fez, name, klass) {
  // Already a FezBase subclass
  if (klass.prototype instanceof FezBase) {
    if (klass.html) {
      klass.html = closeCustomTags(klass.html);
    }
    if (klass.PROPS) {
      Fez.index.ensure(name).props = klass.PROPS;
    }
    return klass;
  }

  // Throwaway instance, read once for config fields (HTML, CSS, PROPS, ...)
  const instance = new klass();
  const userKlass = klass;

  // Each component runs the user class's field initializers on itself, so an
  // arrow field (`inc = () => this.state.n++`) binds to the live instance and an
  // object field (`cache = {}`) is not shared between instances.
  const newKlass = class extends FezBase {
    constructor() {
      super();
      const own = Reflect.construct(userKlass, [], new.target);
      for (const key of Object.keys(this)) {
        if (!Object.hasOwn(own, key)) {
          own[key] = this[key];
        }
      }
      return own;
    }
  };

  // descriptors, so getters and setters stay accessors
  for (const prop of Object.getOwnPropertyNames(klass.prototype)) {
    if (prop !== 'constructor') {
      Object.defineProperty(
        newKlass.prototype,
        prop,
        Object.getOwnPropertyDescriptor(klass.prototype, prop),
      );
    }
  }

  // Map config properties
  const configMap = {
    GLOBAL: 'GLOBAL',
    MOUNT: 'MOUNT',
    NAME: 'nodeName',
    PROPS: 'PROPS',
  };
  for (const [from, to] of Object.entries(configMap)) {
    // instance field (`PROPS = {...}`) or static (`static PROPS = {...}`) on a plain class
    const value = instance[from] || klass[from];
    if (value) {
      newKlass[to] = value;
    }
  }

  // Handle CSS (can be string or function)
  if (instance.CSS) {
    newKlass.css = typeof instance.CSS === 'function' ? instance.CSS() : instance.CSS;
  }

  if (instance.CSS_GLOBAL) {
    newKlass.cssGlobal =
      typeof instance.CSS_GLOBAL === 'function' ? instance.CSS_GLOBAL() : instance.CSS_GLOBAL;
  }

  // Handle HTML (can be string or function)
  if (instance.HTML) {
    const html = typeof instance.HTML === 'function' ? instance.HTML() : instance.HTML;
    newKlass.html = closeCustomTags(html);
  }

  // Handle META (generic metadata object)
  if (instance.META) {
    newKlass.META = instance.META;
    Fez.index.ensure(name).meta = instance.META;
  }

  // Expose PROPS schema on the index for introspection (docs, inspect, tooling)
  if (newKlass.PROPS) {
    Fez.index.ensure(name).props = newKlass.PROPS;
  }

  // GLOBAL only names the instance (window[name]); MOUNT is the singleton body-append
  if (newKlass.GLOBAL && typeof newKlass.GLOBAL !== 'string') {
    Fez.onError(
      'compile',
      `<${name}>: GLOBAL must be a window name string, use MOUNT = true to auto-mount`,
    );
    delete newKlass.GLOBAL;
  }

  // Skip when the page already placed the tag, so a mount is idempotent
  if (newKlass.MOUNT) {
    Fez.onReady(() => {
      if (!document.querySelector(`${name}, .${name}-fez`)) {
        document.body.appendChild(document.createElement(name));
      }
    });
  }

  Fez.consoleLog(`${name} compiled`);
  return newKlass;
}

// =============================================================================
// NODE CONNECTION (Instantiation)
// =============================================================================

/**
 * Initialize component instance from DOM node
 */
function connectNode(name, node) {
  if (!node.isConnected) {
    return;
  }
  if (node.classList?.contains('fez')) {
    return;
  }
  if (!node.parentNode) {
    console.warn(`Fez: ${name} has no parent, skipping`);
    return;
  }

  const klass = Fez.index[name]?.class;
  const newNode = klass.createRootNode(node);
  newNode.classList.add('fez', `${name}-fez`);

  // Replace custom element with component node
  node.parentNode.replaceChild(newNode, node);

  // Create instance
  const fez = new klass();
  fez.UID = ++Fez.instanceCount;
  Fez.instances.set(fez.UID, fez);

  fez.oldRoot = node;
  fez.fezName = name;
  fez.root = newNode;
  fez.props = klass.getProps(node, newNode);
  fez.class = klass;

  // Source snapshot for unkeyed identity matching in the differ. The live DOM
  // is rendered output and never resembles the source again, so this is the
  // only record of what this component was created from (tag + attrs + content).
  newNode._fezSignature = node.outerHTML;

  // Move children (slot content)
  fez.fezSlot(node, newNode);

  newNode.fez = fez;

  // Global component reference. Last connected wins: the differ connects the
  // replacement before the old instance is destroyed, so `||=` would keep a dead one.
  if (klass.GLOBAL) {
    window[klass.GLOBAL] = fez;
  }

  // jQuery compatibility
  if (window.$) {
    fez.$root = $(newNode);
  }

  if (fez.props.id) {
    newNode.setAttribute('id', fez.props.id);
  }

  // Identity attributes the differ matches on: `key` from the compiler or the
  // user, `fez-key` as the explicit opt-in for server-rendered HTML (a matched
  // component is preserved and gets a props refresh instead of destroy +
  // recreate, even when attrs or content changed), `fez-keep` for plain
  // preservation.
  for (const attr of ['key', 'fez-key', 'fez-keep']) {
    const value = node.getAttribute(attr);
    if (value) {
      newNode.setAttribute(attr, value);
    }
  }
  if (node._fezKey !== undefined) {
    newNode._fezKey = node._fezKey;
  }

  // === LIFECYCLE ===

  fez.fezRegister();

  // Capture children before rendering replaces them
  if (fez.root.childNodes.length) {
    fez._fezSlotNodes = Array.from(fez.root.childNodes);
    fez._fezChildNodes = fez._fezSlotNodes.filter((n) => n.nodeType === 1);
  }

  // A throw anywhere in mount leaves no half-built instance behind: it is
  // reported and destroyed, so timers and subscriptions made so far are released
  try {
    fez.fezInit();
    fez.fezRender();
    // onMount may return a cleanup, run on destroy
    const cleanup = fez.onMount(fez.props);
    if (typeof cleanup === 'function') {
      fez.addOnDestroy(cleanup);
    }
    if (fez.GLOBAL_STATE) {
      const entries = Object.entries(fez.GLOBAL_STATE);
      fez.addOnDestroy(() => {
        for (const [key] of entries) {
          Fez.state.delete(key);
        }
      });
      for (const [key, value] of entries) {
        Fez.state.set(key, typeof value === 'function' ? value.call(fez) : value);
      }
    }
    // every prop counts as changed at mount, so a component can load from onRefresh alone
    fez.onRefresh(fez.props, { ...fez._propsRaw });
  } catch (error) {
    // an ancestor onError(error, component) boundary takes it before the global report
    if (!fez.fezBubbleError(error)) {
      fez.fezError('mount', `failed to mount: ${error.message}`, error);
    }
    fez.fezOnDestroy();
    return;
  }

  // Form submit handling: delegated, so a form rendered later (inside {#if})
  // or replaced by the morph still submits here. A child component's own form
  // is left to the child.
  if (fez.onSubmit) {
    fez.on(fez.root, 'submit', (e) => {
      if (e.target.closest('.fez') !== fez.root) {
        return;
      }
      e.preventDefault();
      fez.onSubmit(fez.formData(e.target));
    });
  }
}
